import React, { useMemo, useState } from 'react'
import { Dialog, DialogContent } from '@mui/material'
import { t } from 'i18next'
import { companiesId } from 'Utils/constants'
import { searchCompanyName } from 'Utils/functions'
import { CustomCheckBox } from 'Components/CustomCheckBox'
import FilterSelector from 'Components/FilterSelector'
import searchIcon from 'Assets/Images/BuscarCotizacion/search.svg'
import '../../pickingFlowShared.css'
import './AddBranchModal.css'

const COMPANY_OPTIONS = Object.values(companiesId)
  .filter(Boolean)
  .map(CompanyId => ({ CompanyId, Name: searchCompanyName({ CompanyId }) }))

function AddBranchModal({ open, availableBranches, onAdd, onClose }) {
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    COMPANY_OPTIONS[0].CompanyId
  )
  const [searchText, setSearchText] = useState('')
  const [selectedIds, setSelectedIds] = useState([])

  const resetFilters = () => {
    setSelectedCompanyId(COMPANY_OPTIONS[0].CompanyId)
    setSearchText('')
    setSelectedIds([])
  }

  const handleClose = () => {
    resetFilters()
    onClose()
  }

  const filteredBranches = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()

    return availableBranches.filter(branch => {
      const matchesCompany =
        !selectedCompanyId || branch.CompanyId === selectedCompanyId
      const matchesSearch =
        !normalizedSearch ||
        (branch.Name || '').toLowerCase().includes(normalizedSearch)

      return matchesCompany && matchesSearch
    })
  }, [availableBranches, selectedCompanyId, searchText])

  const handleToggleBranch = branchId => {
    setSelectedIds(prev =>
      prev.includes(branchId)
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    )
  }

  const handleSelectAll = () => {
    const filteredIds = filteredBranches.map(branch => branch.BranchOfficeId)
    setSelectedIds(prev => Array.from(new Set([...prev, ...filteredIds])))
  }

  const handleAdd = () => {
    const selectedBranches = availableBranches.filter(branch =>
      selectedIds.includes(branch.BranchOfficeId)
    )
    resetFilters()
    onAdd(selectedBranches)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="modal-add-picking-flow-branch">
      <DialogContent
        className="add-picking-flow-branch-modal"
        data-testid="content-add-picking-flow-branch">
        <h4
          className="add-picking-flow-branch-title"
          data-testid="title-add-picking-flow-branch">
          {t('PICKING_FLOW_SETTINGS.MODAL.TITLE')}
        </h4>

        <div
          className="add-picking-flow-branch-filters"
          data-testid="filters-add-picking-flow-branch">
          <div className="add-picking-flow-branch-company-filter">
            <FilterSelector
              options={COMPANY_OPTIONS}
              getOptionValue={company => company.CompanyId}
              getOptionLabel={company => company.Name}
              value={selectedCompanyId}
              onChange={company => setSelectedCompanyId(company.CompanyId)}
              placeholder={t(
                'PICKING_FLOW_SETTINGS.MODAL.COMPANY_FILTER_PLACEHOLDER'
              )}
              testId="add-picking-flow-company"
            />
          </div>

          <div className="picking-flow-search-group add-picking-flow-branch-search">
            <input
              type="text"
              data-testid="input-add-picking-flow-search"
              placeholder={t('PICKING_FLOW_SETTINGS.MODAL.SEARCH_PLACEHOLDER')}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
            />
            <img
              src={searchIcon}
              className="picking-flow-search-icon"
              alt="search"
            />
          </div>

          <button
            type="button"
            className="picking-flow-btn-outline"
            data-testid="btn-select-all-picking-flow-branches"
            onClick={handleSelectAll}
            disabled={filteredBranches.length === 0}>
            {t('PICKING_FLOW_SETTINGS.MODAL.SELECT_ALL')}
          </button>
        </div>

        <ul
          className="add-picking-flow-branch-list"
          data-testid="list-available-picking-flow-branches">
          {filteredBranches.length > 0 ? (
            filteredBranches.map(branch => {
              const isSelected = selectedIds.includes(branch.BranchOfficeId)

              return (
                <li
                  key={branch.BranchOfficeId}
                  className="add-picking-flow-branch-item"
                  data-testid={`item-add-picking-flow-branch-${branch.BranchOfficeId}`}>
                  <CustomCheckBox
                    key={branch.BranchOfficeId}
                    checked={isSelected}
                    data-testid={`checkbox-add-picking-flow-branch-${branch.BranchOfficeId}`}
                    onChange={() => handleToggleBranch(branch.BranchOfficeId)}
                  />
                  <span
                    data-testid={`name-add-picking-flow-branch-${branch.BranchOfficeId}`}>
                    {branch.Name}
                  </span>
                </li>
              )
            })
          ) : (
            <li
              className="add-picking-flow-branch-empty"
              data-testid="empty-add-picking-flow-branches">
              {t('PICKING_FLOW_SETTINGS.MODAL.NO_RESULTS')}
            </li>
          )}
        </ul>

        <div
          className="add-picking-flow-branch-actions"
          data-testid="actions-add-picking-flow-branch">
          <button
            type="button"
            className="picking-flow-btn-outline"
            data-testid="btn-cancel-add-picking-flow-branch"
            onClick={handleClose}>
            {t('PICKING_FLOW_SETTINGS.MODAL.CANCEL')}
          </button>
          <button
            type="button"
            className="picking-flow-btn-solid"
            data-testid="btn-confirm-add-picking-flow-branch"
            onClick={handleAdd}
            disabled={selectedIds.length === 0}>
            {t('PICKING_FLOW_SETTINGS.MODAL.ADD')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddBranchModal
